import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import type { EligibilityDefinition } from '../../types/settings';
import './Eligibility.css';

export function Eligibility() {
  const queryClient = useQueryClient();
  const [eligibilityDefs, setEligibilityDefs] = useState<EligibilityDefinition[]>([]);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string>('');
  const [valuesText, setValuesText] = useState<Record<number, string>>({});

  const { data: eligibilityDefsData, isLoading } = useQuery({
    queryKey: ['eligibilityDefinitions'],
    queryFn: adminApi.getEligibilityDefinitions,
  });

  useEffect(() => {
    if (eligibilityDefsData) {
      setEligibilityDefs(eligibilityDefsData);
    }
  }, [eligibilityDefsData]);

  const saveMutation = useMutation({
    mutationFn: (defs: EligibilityDefinition[]) => adminApi.updateEligibilityDefinitions(defs),
    onMutate: () => setSaveStatus('saving'),
    onSuccess: (saved) => {
      setEligibilityDefs(saved);
      setSaveStatus('saved');
      queryClient.invalidateQueries({ queryKey: ['eligibilityDefinitions'] });
      setTimeout(() => setSaveStatus('idle'), 2000);
    },
    onError: (err) => {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    },
  });

  return (
    <div className="settings-section">
      <div className="section-header">
        <h2>Eligibility Rules</h2>
        <p className="section-description">
          Define eligibility rules that can be toggled per user. Each eligibility has a set of allowed values and a default.
        </p>
      </div>

      {isLoading ? (
        <div className="loading">Loading eligibility definitions...</div>
      ) : (
        <>
          <div className="features-list">
            {eligibilityDefs.map((eligibility, idx) => (
              <div key={idx} className="feature-card">
                <div className="feature-card-top">
                  <div className="feature-fields-grid">
                    <div className="feature-field">
                      <label className="feature-field-label">Name (unique key)</label>
                      <input
                        type="text"
                        className="feature-field-input"
                        value={eligibility.name}
                        onChange={(e) => {
                          const updated = [...eligibilityDefs];
                          updated[idx] = { ...updated[idx], name: e.target.value };
                          setEligibilityDefs(updated);
                        }}
                        placeholder="e.g. voice_chat"
                        readOnly={!!eligibilityDefsData?.find(f => f.name === eligibility.name)}
                      />
                    </div>
                    <div className="feature-field">
                      <label className="feature-field-label">Display Name</label>
                      <input
                        type="text"
                        className="feature-field-input"
                        value={eligibility.displayName}
                        onChange={(e) => {
                          const updated = [...eligibilityDefs];
                          updated[idx] = { ...updated[idx], displayName: e.target.value };
                          setEligibilityDefs(updated);
                        }}
                        placeholder="Voice Chat"
                      />
                    </div>
                  </div>
                  <button
                    className="feature-remove-btn"
                    onClick={() => {
                      const updated = eligibilityDefs.filter((_, i) => i !== idx);
                      setEligibilityDefs(updated);
                    }}
                    title="Remove eligibility"
                  >
                    &times;
                  </button>
                </div>
                <div className="feature-field feature-field-full">
                  <label className="feature-field-label">Description</label>
                  <input
                    type="text"
                    className="feature-field-input"
                    value={eligibility.description}
                    onChange={(e) => {
                      const updated = [...eligibilityDefs];
                      updated[idx] = { ...updated[idx], description: e.target.value };
                      setEligibilityDefs(updated);
                    }}
                    placeholder="What this eligibility controls"
                  />
                </div>
                <div className="feature-fields-grid">
                  <div className="feature-field">
                    <label className="feature-field-label">Values (comma-separated)</label>
                    <input
                      type="text"
                      className="feature-field-input"
                      value={valuesText[idx] ?? eligibility.enableValues.join(', ')}
                      onChange={(e) => {
                        setValuesText(prev => ({ ...prev, [idx]: e.target.value }));
                      }}
                      onBlur={() => {
                        const raw = valuesText[idx];
                        if (raw === undefined) return;
                        const values = raw.split(',').map(v => v.trim()).filter(v => v);
                        const updated = [...eligibilityDefs];
                        updated[idx] = { ...updated[idx], enableValues: values.length > 0 ? values : ['enabled', 'disabled'] };
                        if (values.length > 0 && !values.includes(updated[idx].defaultValue)) {
                          updated[idx] = { ...updated[idx], defaultValue: values[0] };
                        }
                        setEligibilityDefs(updated);
                        setValuesText(prev => { const next = { ...prev }; delete next[idx]; return next; });
                      }}
                      placeholder="enabled, disabled"
                    />
                  </div>
                  <div className="feature-field">
                    <label className="feature-field-label">Default Value</label>
                    <select
                      className="feature-field-input"
                      value={eligibility.defaultValue}
                      onChange={(e) => {
                        const updated = [...eligibilityDefs];
                        updated[idx] = { ...updated[idx], defaultValue: e.target.value };
                        setEligibilityDefs(updated);
                      }}
                    >
                      {eligibility.enableValues.map(v => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                  </div>
                  <div className="feature-field">
                    <label className="feature-field-label">Reset Period</label>
                    <select
                      className="feature-field-input"
                      value={eligibility.resetPeriod || 'none'}
                      onChange={(e) => {
                        const updated = [...eligibilityDefs];
                        // 'plan' (the plan's own paid month) is plan-only: set it per plan on Definitions, never here.
                        const val = e.target.value as 'none' | 'day' | 'week' | 'month';
                        updated[idx] = { ...updated[idx], resetPeriod: val === 'none' ? undefined : val };
                        setEligibilityDefs(updated);
                      }}
                    >
                      <option value="none">Lifetime</option>
                      <option value="day">Per Day</option>
                      <option value="week">Per Week</option>
                      <option value="month">Per Month</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="feature-actions">
            <button
              className="feature-add-btn"
              onClick={() => {
                setEligibilityDefs([...eligibilityDefs, {
                  name: '',
                  displayName: '',
                  description: '',
                  enableValues: ['enabled', 'disabled'],
                  defaultValue: 'disabled',
                }]);
              }}
            >
              + Add Eligibility
            </button>
            <button
              className="feature-save-btn"
              onClick={() => saveMutation.mutate(eligibilityDefs)}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving...' : 'Save Eligibility Rules'}
            </button>
          </div>
          {saveStatus === 'saved' && <span className="status-saved feature-status">Eligibility rules saved!</span>}
          {saveStatus === 'error' && <span className="status-error feature-status">{saveError || 'Failed to save'}</span>}
        </>
      )}
    </div>
  );
}
